import { useState } from "react";
import { ArrowRight, Users, UserPlus, Headset, TrendingUp, LogOut, Image as ImageIcon } from "lucide-react";

const HR = {
  name: "HR & People Ops",
  title: "Reimagine the Employee Lifecycle with Agentic AI",
  subtitle:
    "Transform how HR teams hire, onboard, support, develop and manage employees with intelligent AI agents that understand workforce context, make decisions, coordinate work and take action across enterprise systems.",
  useCaseName: "Hire-to-Retire",
  journey: ["Attract & Hire", "Onboard", "Serve", "Develop & Retain", "Offboard"],
  agents: [
    { icon: Users, title: "Talent Acquisition", desc: "Evaluate, reason, recommend and coordinate hiring decisions across the hiring journey." },
    { icon: UserPlus, title: "Employee Onboarding", desc: "Coordinate onboarding across HR, IT, Finance and business teams and resolve blockers." },
    { icon: Headset, title: "HR Service Delivery", desc: "Understand employee needs, interpret policies and execute the right action across systems." },
    { icon: TrendingUp, title: "Performance & Talent", desc: "Turn workforce signals into development, mobility, retention and talent actions." },
    { icon: LogOut, title: "Employee Offboarding", desc: "Securely orchestrate a compliant exit — knowledge transfer, access, assets and payroll." },
  ],
};

const FUNCTIONS = [
  { id: "sales", name: "Sales" },
  { id: "marketing", name: "Marketing" },
  { id: "finance", name: "Finance & Accounting" },
  { id: "supplychain", name: "Supply Chain" },
  { id: "cs", name: "Customer Service" },
  { id: "hr", name: "HR & People Ops", data: HR },
];

const Thumb = () => (
  <div className="rounded-xl border border-dashed border-[#8A5730]/35 bg-[#F7F5F0] aspect-[4/3] flex flex-col items-center justify-center text-center px-6">
    <div className="w-10 h-10 rounded-lg bg-[#8A5730]/10 text-[#8A5730] flex items-center justify-center mb-3"><ImageIcon size={18} strokeWidth={1.8} /></div>
    <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#8A5730]">Demo application</p>
    <p className="text-[12px] text-[#3E2818]/45 mt-1.5">Thumbnail placeholder</p>
  </div>
);

const ContentPanel = ({ f }) => {
  if (!f.data) return (
    <div className="rounded-2xl border border-dashed border-[#3E2818]/20 bg-white/50 py-20 text-center">
      <p className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#8A5730]">{f.name}</p>
      <p className="text-sm text-[#3E2818]/50 mt-2">Content coming soon — HR shown as the example.</p>
    </div>
  );
  const d = f.data;
  return (
    <div className="rounded-2xl bg-white border border-[#3E2818]/10 p-6 lg:p-8">
      <div className="grid lg:grid-cols-[1fr_0.5fr] gap-6 items-start">
        <div>
          <h3 className="font-display text-2xl lg:text-3xl font-medium text-[#3E2818] tracking-tight leading-tight">{d.title}</h3>
          <p className="text-sm text-[#3E2818]/65 mt-3 leading-relaxed">{d.subtitle}</p>
        </div>
        <Thumb />
      </div>
      <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl bg-[#F7F5F0] p-4 border border-[#3E2818]/8">
        <span className="font-mono2 text-[10px] uppercase tracking-[0.22em] text-[#8A5730] shrink-0">{d.useCaseName}</span>
        <div className="flex flex-wrap items-center gap-2">
          {d.journey.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <span className="text-[11px] font-semibold rounded-full px-3 py-1.5 bg-[#8A5730]/8 border border-[#8A5730]/25 text-[#3E2818]">{s}</span>
              {i < d.journey.length - 1 && <ArrowRight size={13} className="text-[#8A5730]" />}
            </div>
          ))}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-6">
        {d.agents.map((a) => (
          <div key={a.title} className="rounded-xl bg-white border border-[#3E2818]/10 p-5 hover:border-[#8A5730]/35 hover:shadow-[0_18px_44px_-28px_rgba(62,40,24,0.4)] transition-all duration-300">
            <div className="w-9 h-9 rounded-lg bg-[#8A5730]/10 text-[#8A5730] flex items-center justify-center mb-3"><a.icon size={16} strokeWidth={1.9} /></div>
            <p className="font-semibold text-[#3E2818] text-sm">{a.title}</p>
            <p className="text-[12px] text-[#3E2818]/55 mt-1.5 leading-snug">{a.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---- Horizontal nav variants ---- */
const NavUnderline = ({ active, setActive }) => (
  <div className="flex flex-wrap gap-x-7 gap-y-2 border-b border-[#3E2818]/12 mb-8">
    {FUNCTIONS.map((x) => (
      <button key={x.id} onClick={() => setActive(x.id)}
        className={`relative pb-3 text-[14px] font-semibold transition ${active === x.id ? "text-[#3E2818]" : "text-[#3E2818]/45 hover:text-[#3E2818]/70"}`}>
        {x.name}
        {active === x.id && <span className="absolute left-0 -bottom-px h-[2px] w-full bg-[#8A5730]" />}
      </button>
    ))}
  </div>
);

const NavPills = ({ active, setActive }) => (
  <div className="flex flex-wrap gap-2 mb-8">
    {FUNCTIONS.map((x) => (
      <button key={x.id} onClick={() => setActive(x.id)}
        className={`rounded-full px-4 py-2 text-[13px] font-semibold border transition ${active === x.id ? "bg-[#3E2818] text-[#F7F5F0] border-[#3E2818]" : "bg-white text-[#3E2818]/70 border-[#3E2818]/12 hover:border-[#8A5730]/40"}`}>
        {x.name}
      </button>
    ))}
  </div>
);

const NavSegmented = ({ active, setActive }) => (
  <div className="inline-flex flex-wrap gap-1 p-1 rounded-full bg-[#3E2818]/5 border border-[#3E2818]/10 mb-8">
    {FUNCTIONS.map((x) => (
      <button key={x.id} onClick={() => setActive(x.id)}
        className={`rounded-full px-3.5 py-2 text-[12.5px] font-semibold transition ${active === x.id ? "bg-[#3E2818] text-[#F7F5F0]" : "text-[#3E2818]/60 hover:text-[#3E2818]"}`}>
        {x.name}
      </button>
    ))}
  </div>
);

const NavBoxes = ({ active, setActive }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-8">
    {FUNCTIONS.map((x, i) => (
      <button key={x.id} onClick={() => setActive(x.id)}
        className={`text-left rounded-xl px-4 py-3 border transition ${active === x.id ? "bg-[#FBF2E1] border-[#8A5730]/45" : "bg-white border-[#3E2818]/10 hover:border-[#8A5730]/30"}`}>
        <span className="font-mono2 text-[10px] text-[#8A5730]">{String(i + 1).padStart(2, "0")}</span>
        <p className="text-[13px] font-semibold text-[#3E2818] leading-tight mt-0.5">{x.name}</p>
      </button>
    ))}
  </div>
);

const NAVS = [
  { id: "underline", label: "Nav A · Underline Tabs", comp: NavUnderline, note: "Classic editorial underline tabs — text with a caramel underline on the active function. Minimal and enterprise." },
  { id: "pills", label: "Nav B · Pills", comp: NavPills, note: "Rounded pill buttons; the active function is a solid dark pill. Friendly and clear." },
  { id: "segmented", label: "Nav C · Segmented Control", comp: NavSegmented, note: "A connected segmented control in one rounded container; compact and app-like." },
  { id: "boxes", label: "Nav D · Numbered Boxes", comp: NavBoxes, note: "A 6-up row of numbered boxes; the active one is gold-tinted. Structured and scannable." },
];

export default function FunctionsNavOptions() {
  const [nav, setNav] = useState("underline");
  const [active, setActive] = useState("hr");
  const NavComp = NAVS.find((n) => n.id === nav).comp;
  const note = NAVS.find((n) => n.id === nav).note;
  const f = FUNCTIONS.find((x) => x.id === active);
  return (
    <div className="min-h-screen bg-[#161009] text-[#F7F5F0]">
      <div className="max-w-6xl mx-auto px-5 lg:px-8 pt-10">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#E8AA68]">Section 4 — Horizontal Nav Options</p>
        <h1 className="font-display text-2xl lg:text-4xl font-medium mt-2 tracking-tight">Pick a top navigation style</h1>
        <p className="text-sm text-white/55 mt-3 max-w-2xl leading-relaxed">Same content panel (Option 2), now with top horizontal navigation. Switch the nav style below, and click functions to see it in action.</p>
        <div className="flex flex-wrap gap-2 mt-6">
          {NAVS.map((n) => (
            <button key={n.id} data-testid={`nav-style-${n.id}`} onClick={() => setNav(n.id)}
              className={`rounded-full px-4 py-2 text-[12px] font-semibold border transition ${nav === n.id ? "bg-[#E8AA68] text-[#211307] border-[#E8AA68]" : "bg-white/5 text-white/70 border-white/15 hover:border-white/40"}`}>
              {n.label}
            </button>
          ))}
        </div>
        <p className="text-[12px] text-white/45 mt-4 max-w-3xl leading-relaxed">{note}</p>
      </div>
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 pb-14 pt-6">
        <div className="rounded-2xl overflow-hidden border border-white/10 bg-[#F7F5F0] p-6 lg:p-10 shadow-[0_40px_120px_-40px_rgba(0,0,0,0.9)]">
          <div className="max-w-4xl mb-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-[#8A5730]" />
              <span className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#8A5730]">Solutions by Function</span>
            </div>
            <h2 className="font-display font-medium tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#3E2818] leading-[1.08]">Build for Your Core Functions</h2>
          </div>
          <NavComp active={active} setActive={setActive} />
          <ContentPanel f={f} />
        </div>
      </div>
    </div>
  );
}
